import { DropdownMenu } from "@package/subframe/components/DropdownMenu";
import { IconButton } from "@package/subframe/components/IconButton";
import * as SubframeCore from "@subframe/core";
import { FeatherCamera, FeatherTrash2, FeatherUpload } from "@subframe/core";
import { useRef } from "react";
import { toast } from "sonner";

import {
  useEmployeePhoto,
  useIsEmployeePhotoChanging,
} from "@/hooks/use-employee-photo";
import {
  PHOTO_MIME_TYPES,
  photoFileProblem,
} from "@/lib/people-key-information";

interface EmployeePhotoMenuProps {
  nurseryId: string;
  userId: string;
  name: string;
  hasPhoto: boolean;
}

/**
 * The camera button on the profile photo. A photo is saved the moment it is
 * chosen or removed, so it lives here rather than in a form with a Cancel.
 */
export function EmployeePhotoMenu({
  nurseryId,
  userId,
  name,
  hasPhoto,
}: EmployeePhotoMenuProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const { upload, remove } = useEmployeePhoto(nurseryId, userId);
  const isBusy = useIsEmployeePhotoChanging(userId);

  const handleFile = (file: File | undefined) => {
    if (!file) return;
    const problem = photoFileProblem(file);
    if (problem) {
      toast.error(problem);
      return;
    }
    upload.mutate(file);
  };

  return (
    <>
      <SubframeCore.DropdownMenu.Root>
        <SubframeCore.DropdownMenu.Trigger asChild={true}>
          <IconButton
            variant="neutral-secondary"
            size="medium"
            icon={<FeatherCamera />}
            loading={isBusy}
            disabled={isBusy}
            aria-label={`Change photo of ${name}`}
            data-testid="profile-photo-button"
            className="rounded-full border border-solid border-neutral-200 bg-default-background shadow-sm"
          />
        </SubframeCore.DropdownMenu.Trigger>
        <SubframeCore.DropdownMenu.Portal>
          <SubframeCore.DropdownMenu.Content
            side="bottom"
            align="start"
            sideOffset={4}
            asChild={true}
          >
            <DropdownMenu>
              <DropdownMenu.DropdownItem
                icon={<FeatherUpload />}
                onSelect={() => inputRef.current?.click()}
              >
                {hasPhoto ? "Replace photo" : "Add photo"}
              </DropdownMenu.DropdownItem>
              {hasPhoto && (
                <DropdownMenu.DropdownItem
                  icon={<FeatherTrash2 />}
                  onSelect={() => remove.mutate({ nurseryId, userId })}
                >
                  Remove photo
                </DropdownMenu.DropdownItem>
              )}
            </DropdownMenu>
          </SubframeCore.DropdownMenu.Content>
        </SubframeCore.DropdownMenu.Portal>
      </SubframeCore.DropdownMenu.Root>
      <input
        ref={inputRef}
        type="file"
        accept={PHOTO_MIME_TYPES.join(",")}
        className="hidden"
        data-testid="photo-input"
        onChange={(event) => {
          handleFile(event.target.files?.[0]);
          event.target.value = "";
        }}
      />
    </>
  );
}
